import { apiClient } from './client';
import { extractBlobErrorMessage, getPdfBlob } from './payments';
import { downloadBlob } from '../utils/downloadBlob';
import { PaginatedResponse } from '../types/pagination';
import {
  AssignableApplication,
  BatchEligibleCandidate,
  BulkCommResult,
  BulkStatusResult,
  DepartureBatchFilter,
  DeparturePaymentEligibleStudent,
  JourneyApplication,
  ReadyToFlyDashboardStats,
  TravelBatch,
  TravelDetail,
  TravelDetailInput,
  TravelProfile,
} from '../types/readyToFly';

export type BatchSize = 15 | 20 | 50 | 100;

export const listEligible = async (limit: BatchSize): Promise<BatchEligibleCandidate[]> => {
  const { data } = await apiClient.get('/ready-to-fly/eligible', { params: { limit } });
  return data.data;
};

export const listBatches = async (): Promise<TravelBatch[]> => {
  const { data } = await apiClient.get('/ready-to-fly/batches');
  return data.data;
};

export const getBatch = async (id: number): Promise<TravelBatch> => {
  const { data } = await apiClient.get(`/ready-to-fly/batches/${id}`);
  return data.data;
};

/** Manual Batch allocation (Phase 61) — a batch is created empty (no
 * `limit`/auto-select); students are assigned to it afterwards via
 * `assignApplicationsToBatch`. */
export const createBatch = async (payload: {
  name?: string;
  departureDate?: string;
  capacity?: number;
}): Promise<TravelBatch> => {
  const { data } = await apiClient.post('/ready-to-fly/batches', payload);
  return data.data;
};

export const updateBatch = async (
  id: number,
  payload: { name?: string | null; departureDate?: string | null; capacity?: number | null },
): Promise<TravelBatch> => {
  const { data } = await apiClient.patch(`/ready-to-fly/batches/${id}`, payload);
  return data.data;
};

export const deleteBatch = async (id: number): Promise<void> => {
  await apiClient.delete(`/ready-to-fly/batches/${id}`);
};

export interface AssignableParams {
  page: number;
  pageSize: number;
  search?: string;
  universityId?: number;
  countryId?: number;
  intakeId?: number;
}

export const listAssignable = async (params: AssignableParams): Promise<PaginatedResponse<AssignableApplication>> => {
  const { data } = await apiClient.get('/ready-to-fly/assignable', { params });
  return data;
};

/** Batch eligibility correction (Phase 63) — `studentIds` covers a
 * Registered student who has no formal Application yet at all; the
 * server provisions one from their preferred University/Programme. */
export const assignApplicationsToBatch = async (
  batchId: number,
  payload: { applicationIds: number[]; studentIds: number[] },
): Promise<TravelBatch> => {
  const { data } = await apiClient.post(`/ready-to-fly/batches/${batchId}/assign`, payload);
  return data.data;
};

/** Batch progress correction (Phase 64) — removes a single student from
 * a batch without moving them into another one. */
export const removeApplicationFromBatch = async (batchId: number, applicationId: number): Promise<TravelBatch> => {
  const { data } = await apiClient.delete(`/ready-to-fly/batches/${batchId}/members/${applicationId}`);
  return data.data;
};

const openDocument = async (path: string) => {
  const response = await apiClient.get(path, { responseType: 'text' });
  const win = window.open('', '_blank');
  win?.document.write(response.data as string);
};

export const openDepartureList = (batchId: number) => openDocument(`/ready-to-fly/batches/${batchId}/departure-list`);
export const openAirportPickup = (batchId: number) => openDocument(`/ready-to-fly/batches/${batchId}/airport-pickup`);
export const openTravelManifest = (batchId: number) =>
  openDocument(`/ready-to-fly/batches/${batchId}/travel-manifest`);

export interface JourneyParams {
  page: number;
  pageSize: number;
  status?: string;
  universityId?: number;
  countryId?: number;
  pickupRequired?: boolean;
  search?: string;
}

export const getJourney = async (params: JourneyParams): Promise<PaginatedResponse<JourneyApplication>> => {
  const { data } = await apiClient.get('/ready-to-fly/journey', { params });
  return data;
};

export const getReadyToFlyDashboard = async (): Promise<ReadyToFlyDashboardStats> => {
  const { data } = await apiClient.get('/ready-to-fly/dashboard');
  return data.data;
};

export const getTravelProfile = async (applicationId: number): Promise<TravelProfile> => {
  const { data } = await apiClient.get(`/ready-to-fly/applications/${applicationId}`);
  return data.data;
};

export const updateTravelDetail = async (applicationId: number, payload: TravelDetailInput): Promise<TravelDetail> => {
  const { data } = await apiClient.patch(`/ready-to-fly/applications/${applicationId}/travel-detail`, payload);
  return data.data;
};

export const toggleChecklistItem = async (
  applicationId: number,
  list: 'departure' | 'arrival',
  key: string,
  checked: boolean,
): Promise<TravelDetail> => {
  const { data } = await apiClient.patch(`/ready-to-fly/applications/${applicationId}/checklist`, {
    list,
    key,
    checked,
  });
  return data.data;
};

export const bulkMarkReady = async (applicationIds: number[]): Promise<BulkStatusResult> => {
  const { data } = await apiClient.post('/ready-to-fly/bulk/mark-ready', { applicationIds });
  return data.data;
};

export const bulkAssignFlight = async (
  payload: { applicationIds: number[]; departureDate?: string; arrivalDate?: string } & TravelDetailInput,
): Promise<BulkStatusResult> => {
  const { data } = await apiClient.post('/ready-to-fly/bulk/assign-flight', payload);
  return data.data;
};

export const openBulkPrint = async (applicationIds: number[]): Promise<void> => {
  const response = await apiClient.post(
    '/ready-to-fly/bulk/print',
    { applicationIds },
    { responseType: 'text' },
  );
  const win = window.open('', '_blank');
  win?.document.write(response.data as string);
};

export const bulkSendEmail = async (payload: {
  applicationIds: number[];
  templateId?: number;
  subject?: string;
  body?: string;
}): Promise<BulkCommResult> => {
  const { data } = await apiClient.post('/ready-to-fly/bulk/email', payload);
  return data.data;
};

export const bulkSendWhatsApp = async (payload: {
  applicationIds: number[];
  templateId?: number;
  body?: string;
}): Promise<BulkCommResult> => {
  const { data } = await apiClient.post('/ready-to-fly/bulk/whatsapp', payload);
  return data.data;
};

export interface DeparturePaymentEligibleParams {
  page: number;
  pageSize: number;
  search?: string;
  universityId?: number;
  intakeId?: number;
  branchId?: number;
  batch?: DepartureBatchFilter;
}

/** Batches to Departures and Ready to Fly's Fully Paid tab both read this
 * one endpoint, so they always list the same students. */
export const listDeparturePaymentEligible = async (
  params: DeparturePaymentEligibleParams,
): Promise<PaginatedResponse<DeparturePaymentEligibleStudent>> => {
  const { data } = await apiClient.get('/ready-to-fly/departure-payment-eligible', { params });
  return data;
};

export type DepartureExportList = 'travel-agent' | 'detailed';
export type DepartureExportFormat = 'pdf' | 'xlsx';

const DEPARTURE_EXPORT_FILENAMES: Record<DepartureExportList, string> = {
  'travel-agent': 'travel-agent-list',
  detailed: 'detailed-student-list',
};

/** Batches to Departures exports (Phase 94) — the same filters as the list
 * (minus pagination); the server builds the file from the caller's own
 * branch-scoped result set. */
export const downloadDepartureExport = async (
  list: DepartureExportList,
  format: DepartureExportFormat,
  filters: Omit<DeparturePaymentEligibleParams, 'page' | 'pageSize'>,
): Promise<void> => {
  try {
    const response = await apiClient.get('/ready-to-fly/departure-payment-eligible/export', {
      params: { ...filters, list, format },
      responseType: 'blob',
    });
    downloadBlob(response.data, `${DEPARTURE_EXPORT_FILENAMES[list]}-${new Date().toISOString().slice(0, 10)}.${format}`);
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, 'Failed to export the list'));
  }
};

/** Ready to Fly → batch "Student profiles" PDF (Phase 94). */
export const getBatchProfilesPdfBlob = (batchId: number) =>
  getPdfBlob(`/ready-to-fly/batches/${batchId}/student-profiles.pdf`, 'Failed to generate the student profiles PDF');
